import { useState, useEffect, useMemo } from 'react'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { getStudentDetail } from './api/studentsApi'
import { getStudentPerformance, deleteStudentPerformance } from './api/studentScreensApi'

const PAGE_SIZES = [10, 25, 50, 100]

export default function StudentPerformanceScreen() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const studentId = searchParams.get('id')
  const [student, setStudent] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [pageSize, setPageSize] = useState(25)
  const [entries, setEntries] = useState([])

  useEffect(() => { loadStudent(); loadPerformance() }, [studentId])

  const loadStudent = async () => {
    setIsLoading(true)
    try {
      const data = await getStudentDetail(studentId)
      setStudent(data)
    } catch {
      setStudent({ id: studentId, firstName: 'Student', lastName: '' })
    } finally {
      setIsLoading(false)
    }
  }

  const loadPerformance = async () => {
    try {
      const rows = await getStudentPerformance(studentId)
      setEntries(rows || [])
    } catch {
      setEntries([])
    }
  }

  const handleDelete = async (perfId) => {
    try {
      await deleteStudentPerformance(studentId, perfId)
      await loadPerformance()
    } catch {
      // ignore
    }
  }

  const pagedEntries = Array.isArray(entries) ? entries.slice(0, pageSize) : []

  const subjectAverages = useMemo(() => {
    const map = {}
    ;(Array.isArray(entries) ? entries : []).forEach((e) => {
      if (!map[e.subject]) map[e.subject] = { subject: e.subject, total: 0, count: 0 }
      map[e.subject].total += e.percent || 0
      map[e.subject].count += 1
    })
    return Object.values(map).map((s) => ({ subject: s.subject, average: Math.round((s.total / s.count) * 10) / 10 }))
  }, [entries])

  const goBack = () => navigate('/admission/students/list')
  const name = student ? `${student.firstName || ''} ${student.lastName || ''}`.trim() : 'Student'
  const rollNo = student?.rollNo || '—'
  const idCardNo = student?.idCardNo || '—'

  return (
    <div className="mx-auto flex w-full max-w-[1480px] flex-col gap-6 pb-8">
      <section className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-2 text-sm text-on-surface-variant">
            <span>Admission</span>
            <span className="material-symbols-outlined text-xs">chevron_right</span>
            <span>Students</span>
            <span className="material-symbols-outlined text-xs">chevron_right</span>
            <span className="font-semibold text-primary">Performance</span>
          </div>
          <h1 className="mt-2 text-3xl font-bold text-on-surface">Performance — {name}</h1>
        </div>
        <button type="button" onClick={goBack} className="flex h-10 w-10 items-center justify-center rounded-xl bg-surface-container-high text-on-surface-variant transition-colors hover:bg-surface-container hover:text-on-surface" title="Back">
          <span className="material-symbols-outlined text-[20px]">arrow_back</span>
        </button>
      </section>

      <div className="flex items-center justify-between rounded-2xl bg-primary px-6 py-4">
        <div className="flex items-center gap-3">
          <span className="material-symbols-outlined text-on-primary text-[22px]">trending_up</span>
          <h2 className="text-base font-bold text-on-primary">Performance of {name} (Roll No.{rollNo} - ID Card No.{idCardNo})</h2>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-on-primary/80">Show</span>
          <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))} className="rounded-lg border border-white/30 bg-white px-3 py-1.5 text-xs font-bold text-slate-900 outline-none focus:ring-2 focus:ring-white/40">
            {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <span className="text-xs font-bold text-on-primary/80">entries</span>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">

        <div className="space-y-6">
          <div className="rounded-2xl border border-outline-variant/30 bg-surface-container-lowest overflow-hidden">
            <div className="flex items-center gap-3 rounded-t-2xl bg-primary px-6 py-3">
              <span className="material-symbols-outlined text-on-primary text-[18px]">bar_chart</span>
              <h3 className="text-sm font-bold text-on-primary">Comparative Graph</h3>
            </div>
            <div className="px-6 py-6">
              {pagedEntries.length === 0 ? (
                <p className="text-sm text-on-surface-variant">No performance data to chart.</p>
              ) : (
                <div className="flex flex-col gap-3">
                  {pagedEntries.slice(0, 6).map((entry) => (
                    <div key={entry.id} className="flex items-center gap-3">
                      <span className="w-24 shrink-0 truncate text-[10px] font-bold text-on-surface-variant">
                        {entry.subject}
                      </span>
                      <div className="h-5 flex-1 overflow-hidden rounded bg-surface-container-high">
                        <div
                          className={`h-full rounded ${entry.percent >= 80 ? 'bg-emerald-500' : entry.percent >= 60 ? 'bg-amber-500' : 'bg-red-500'}`}
                          style={{ width: `${Math.max(4, Math.min(100, entry.percent))}%` }}
                        />
                      </div>
                      <span className="w-10 shrink-0 text-right text-[10px] font-bold font-mono text-on-surface">
                        {entry.percent}%
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="rounded-2xl border border-outline-variant/30 bg-surface-container-lowest overflow-hidden">
            <div className="flex items-center gap-3 rounded-t-2xl bg-primary px-6 py-3">
              <span className="material-symbols-outlined text-on-primary text-[18px]">pie_chart</span>
              <h3 className="text-sm font-bold text-on-primary">Subjectwise Graph</h3>
            </div>
            <div className="px-6 py-6">
              {subjectAverages.length === 0 ? (
                <p className="text-sm text-on-surface-variant">No performance data to chart.</p>
              ) : (
                <div className="flex flex-col gap-3">
                  {subjectAverages.map((s) => (
                    <div key={s.subject} className="flex items-center gap-3">
                      <span className="w-24 shrink-0 truncate text-[10px] font-bold text-on-surface-variant">
                        {s.subject}
                      </span>
                      <div className="h-5 flex-1 overflow-hidden rounded bg-surface-container-high">
                        <div
                          className="h-full rounded bg-primary"
                          style={{ width: `${Math.max(4, Math.min(100, s.average))}%` }}
                        />
                      </div>
                      <span className="w-10 shrink-0 text-right text-[10px] font-bold font-mono text-on-surface">
                        {s.average}%
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="overflow-hidden rounded-2xl border border-outline-variant/30 bg-surface-container-lowest">
          <table className="w-full text-left text-xs">
            <thead>
              <tr className="border-b border-outline-variant/30 bg-surface-container-low">
                {['Date', 'TestNo', 'Subject', 'Topic', 'Att. Marks', 'Out Of', '%', 'Rank', 'Highest', 'Average', ''].map((h) => (
                  <th key={h} className="px-3 py-3 text-[10px] font-black uppercase tracking-wider text-on-surface-variant whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={11} className="px-5 py-16 text-center">
                    <span className="material-symbols-outlined animate-spin text-3xl text-primary/40">progress_activity</span>
                    <p className="mt-3 text-xs text-on-surface-variant">Loading performance data...</p>
                  </td>
                </tr>
              ) : pagedEntries.length === 0 ? (
                <tr>
                  <td colSpan={11} className="px-5 py-12">
                    <div className="flex items-center justify-center rounded-xl border-2 border-dashed border-blue-200 bg-blue-50/50 px-6 py-8">
                      <p className="text-center text-xs font-bold leading-relaxed text-blue-600">
                        No entries meet selected criteria — Either there are no entries in the selected module or you have selected invalid filter conditions.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                pagedEntries.map((entry, idx) => (
                  <tr key={entry.id} className={`border-b border-outline-variant/10 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50'} hover:bg-primary/5 transition-colors`}>
                    <td className="px-3 py-2.5 text-on-surface font-mono whitespace-nowrap">{entry.date}</td>
                    <td className="px-3 py-2.5 text-on-surface font-mono">{entry.testNo}</td>
                    <td className="px-3 py-2.5">
                      <span className="inline-flex items-center rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-bold text-blue-700">{entry.subject}</span>
                    </td>
                    <td className="px-3 py-2.5 text-on-surface-variant whitespace-nowrap">{entry.topic}</td>
                    <td className="px-3 py-2.5 text-right font-semibold text-on-surface font-mono">{entry.attendanceMarks}</td>
                    <td className="px-3 py-2.5 text-right text-on-surface-variant font-mono">{entry.outOf}</td>
                    <td className="px-3 py-2.5 text-right font-bold font-mono">
                      <span className={entry.percent >= 80 ? 'text-green-600' : entry.percent >= 60 ? 'text-amber-600' : 'text-red-600'}>
                        {entry.percent}%
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-right font-semibold text-on-surface font-mono">{entry.rank}</td>
                    <td className="px-3 py-2.5 text-right text-on-surface-variant font-mono">{entry.highest}</td>
                    <td className="px-3 py-2.5 text-right text-on-surface-variant font-mono">{entry.average}</td>
                    <td className="px-3 py-2.5 text-right">
                      <button
                        onClick={() => handleDelete(entry.id)}
                        className="inline-flex items-center gap-1 rounded-lg bg-error/10 px-2 py-1 text-[10px] font-bold text-error hover:bg-error/20 transition-colors"
                        title="Delete"
                      >
                        <span className="material-symbols-outlined text-sm">delete</span>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

      </div>

      <section className="mt-2 rounded-2xl border border-outline-variant/20 bg-surface-container-low px-8 py-5">
        <div className="flex flex-col gap-1 text-xs text-on-surface-variant">
          {student?.createdBy && (
            <span>Created by <span className="font-bold text-on-surface">{student.createdBy}</span> on <span className="font-semibold text-on-surface">{student.createdDate}</span></span>
          )}
          {student?.updatedBy && (
            <span>Updated by <span className="font-bold text-on-surface">{student.updatedBy}</span> on <span className="font-semibold text-on-surface">{student.updatedDate}</span></span>
          )}
        </div>
      </section>
    </div>
  )
}
